import Joi from 'joi';

import { TaxRateTypeEnum } from '../../../../shared/enums/tax-rate-type.enum';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { VehicleRoadWorthyCheckRecordCategoryEnum } from '../../enums/vehicle-road-worthy-check-record-category.enum';

/**
 * Schema for existing vehicle roadworthy check record validation.
 */
export const updateVehicleRoadWorthyCheckSchema = () =>
  Joi.object({
    id: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|vehicle-road-worthy-check-id|cannot-be-null',
      }),
      otherwise: Joi.number().required().messages({
        'any.required': 'validations|vehicle-road-worthy-check-id|required',
        'number.base':
          'validations|vehicle-road-worthy-check-id|must-be-number',
      }),
    }),

    code: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|vehicle-road-worthy-check-code|cannot-be-null',
      }),
      otherwise: Joi.string().required().messages({
        'any.required': 'validations|vehicle-road-worthy-check-code|required',
        'string.base':
          'validations|vehicle-road-worthy-check-code|must-be-string',
        'string.empty':
          'validations|vehicle-road-worthy-check-code|cannot-be-empty',
      }),
    }),

    recordCategory: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|vehicle-road-worthy-check-record-category|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(VehicleRoadWorthyCheckRecordCategoryEnum))
        .required()
        .messages({
          'any.required':
            'validations|vehicle-road-worthy-check-record-category|required',
          'any.only':
            'validations|vehicle-road-worthy-check-record-category|invalid-value',
          'string.base':
            'validations|vehicle-road-worthy-check-record-category|must-be-string',
          'string.empty':
            'validations|vehicle-road-worthy-check-record-category|cannot-be-empty',
        }),
    }),

    vehicleId: Joi.when('recordCategory', {
      is: VehicleRoadWorthyCheckRecordCategoryEnum.VEHICLE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-road-worthy-check-vehicle-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required':
            'validations|vehicle-road-worthy-check-vehicle-id|required',
          'number.base':
            'validations|vehicle-road-worthy-check-vehicle-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    startsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-road-worthy-check-starts-at|cannot-be-null',
        }),
        otherwise: Joi.date().required().messages({
          'any.required':
            'validations|vehicle-road-worthy-check-starts-at|required',
          'date.base':
            'validations|vehicle-road-worthy-check-starts-at|must-be-a-valid-date',
        }),
      })
      .prefs({ abortEarly: true }),

    endsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-road-worthy-check-ends-at|cannot-be-null',
        }),
        otherwise: Joi.date().greater(Joi.ref('startsAt')).required().messages({
          'any.required':
            'validations|vehicle-road-worthy-check-ends-at|required',
          'date.base':
            'validations|vehicle-road-worthy-check-ends-at|must-be-a-valid-date',
          'date.greater':
            'validations|vehicle-road-worthy-check-ends-at|cannot-be-before-starts-at',
        }),
      })
      .prefs({ abortEarly: true }),

    notes: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-road-worthy-check-notes|must-be-string',
    }),

    externalReference: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-road-worthy-check-external-reference|must-be-string',
    }),

    taxRateType: Joi.string()
      .valid(...Object.values(TaxRateTypeEnum))
      .optional()
      .empty(null)
      .messages({
        'any.only':
          'validations|vehicle-road-worthy-check-tax-rate-type|invalid-value',
      }),

    taxRate: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-road-worthy-check-tax-rate|must-be-number',
    }),

    isTaxIncluded: Joi.boolean().optional().empty(null),

    amount: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-road-worthy-check-amount|must-be-number',
    }),

    totalNet: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-road-worthy-check-total-net|must-be-number',
    }),

    totalTax: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-road-worthy-check-total-tax|must-be-number',
    }),

    totalGross: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-road-worthy-check-total-gross|must-be-number',
    }),

    tags: createTagsSchema().messages({
      'array.base': 'validations|vehicle-road-worthy-check-tags|invalid-value',
    }),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
